/**
 * Requirement composition across configuration layers.
 *
 * Contract: docs/source-of-truth/worker-service-delivery-architecture.md
 * ("Configuration hierarchy" and "Profile lifecycle").
 *
 * Resolution is additive and restrictive. Walking the layers in order, a layer
 * may:
 *
 *   - add a requirement that no earlier layer declared;
 *   - tighten one that exists (raise its authority, or move it to an earlier
 *     gate stage so it is checked sooner); or
 *   - hide a requirement that is purely advisory.
 *
 * It may not remove, weaken or re-tag a requirement owned by a higher
 * authority. A layer that tries fails profile publication, so the conflict is
 * caught when the profile is published rather than silently resolved at the
 * point of care.
 */

import type { ConfigurationLayer, RequirementAuthority } from './authority';
import {
  canLayerAssertAuthority,
  configurationLayerRank,
  isStrongerAuthority,
  requirementAuthorityRank,
} from './authority';
import type { GateStage } from './decisions';
import { gateStageRank } from './decisions';

// ---------------------------------------------------------------------------
// Requirement code ownership
// ---------------------------------------------------------------------------

/**
 * Some requirement codes have an authority fixed by the system. Registering a
 * code here stops any layer from re-declaring it at a weaker authority and
 * thereby buying it an exception path it should never have had.
 *
 * This closes the gap left open in S0.1: a rank check alone cannot tell a
 * mislabelled strong requirement from a legitimately weak one, because nothing
 * told it which codes are strong. This table is what tells it.
 *
 * Deliberately seeded with structural codes only. Per-profession credential and
 * registration codes are NOT listed yet: each needs an effective-dated
 * primary-source reference before it is asserted as statutory or program-owned,
 * per the engine-change gate. An unregistered code is unconstrained here and is
 * governed by its layer's ceiling alone.
 */
const REQUIREMENT_CODE_OWNERS: Readonly<Record<string, RequirementAuthority>> = {
  // Identity and tenancy. Never overridable: an identity mismatch is a block
  // with no exception path in the design's override taxonomy.
  TENANT_MATCHES_OCCURRENCE: 'STATUTORY',
  CLIENT_MATCHES_INCLUSION: 'STATUTORY',
  WORKER_MATCHES_ASSIGNMENT: 'STATUTORY',
  ACTOR_IDENTITY_SERVER_DERIVED: 'STATUTORY',

  // Program/service-list constraints.
  SERVICE_CODE_PUBLISHED: 'PROGRAM',
  EXECUTION_MODE_ALLOWED_FOR_SERVICE: 'PROGRAM',
  CARE_PLAN_INCLUSION_ACTIVE: 'PROGRAM',
  FINANCE_OWNER_SINGLE: 'PROGRAM',

  // Scheduling facts the taxonomy lists as non-overridable.
  WORKER_NOT_ON_LEAVE: 'PROGRAM',
  NO_OVERLAPPING_ASSIGNMENT: 'PROGRAM',
  CLIENT_NO_GO_ABSENT: 'PROGRAM',
  SERVICE_NOT_CANCELLED: 'PROGRAM',

  // Professional scope. Registering these is a structural classification —
  // "a delegation or registration requirement belongs to the professional
  // class" — not a regulatory assertion that any given profession must hold a
  // given credential. That second question is per service, needs a
  // primary-source reference, and is S3.x pack work.
  //
  // Without these entries a caller could tag a registration requirement as
  // PROVIDER_POLICY, which would buy it an exception path and let an
  // unregistered worker clear the START gate.
  DELEGATION_CURRENT: 'PROFESSIONAL_SCOPE',
  SUPERVISOR_ASSIGNED: 'PROFESSIONAL_SCOPE',
  PROFESSIONAL_REGISTRATION_CURRENT: 'PROFESSIONAL_SCOPE',
  RN_REGISTRATION: 'PROFESSIONAL_SCOPE',
  EN_REGISTRATION: 'PROFESSIONAL_SCOPE',
  ALLIED_PROFESSION_CREDENTIAL: 'PROFESSIONAL_SCOPE',
  THERAPEUTIC_PROFESSION_CREDENTIAL: 'PROFESSIONAL_SCOPE',
  DRIVER_LICENCE_CURRENT: 'PROFESSIONAL_SCOPE',
  WORK_RIGHT_VALID: 'STATUTORY',
  STATUTORY_SCREENING_CLEAR: 'STATUTORY',
};

/**
 * Exported so tests enumerate the real table rather than a copy of it. A code
 * added to `REQUIREMENT_CODE_OWNERS` is then covered automatically.
 */
export const SYSTEM_OWNED_REQUIREMENT_CODES: readonly string[] =
  Object.keys(REQUIREMENT_CODE_OWNERS);

export function ownerAuthorityForRequirementCode(code: string): RequirementAuthority | null {
  return REQUIREMENT_CODE_OWNERS[code] ?? null;
}

export function isSystemOwnedRequirementCode(code: string): boolean {
  return code in REQUIREMENT_CODE_OWNERS;
}

/**
 * Requirement codes a tenant genuinely owns, and which may therefore carry an
 * exact-slot exception.
 *
 * This is an allowlist for the same reason the evidence field vocabulary is:
 * letting a caller assert "this one is only provider policy" is a weakening
 * primitive, and the safe default for an unrecognised code is the stronger
 * authority. An unregistered code resolves as `PROFESSIONAL_SCOPE` and cannot
 * be excepted, so a credential code that nobody has classified yet fails closed
 * rather than becoming waivable by omission.
 *
 * Provider-policy requirements are a real, intended feature — readiness and
 * induction controls a provider sets above the baseline. They are enumerated
 * here so that being waivable is a decision somebody made, not a side effect of
 * the table being incomplete.
 */
const TENANT_POLICY_REQUIREMENT_CODES: readonly string[] = [
  'TENANT_INDUCTION_REFRESHER',
  'MANUAL_HANDLING_REFRESHER',
  'PROVIDER_ORIENTATION_COMPLETE',
  'PROVIDER_POLICY_ACKNOWLEDGED',
  'INFECTION_CONTROL_REFRESHER',
  'VEHICLE_CHECK_COMPLETE',
  'PPE_CONFIRMED',
];

const TENANT_POLICY_CODE_SET: ReadonlySet<string> = new Set(TENANT_POLICY_REQUIREMENT_CODES);

export function isTenantPolicyRequirementCode(code: string): boolean {
  return TENANT_POLICY_CODE_SET.has(code);
}

export { TENANT_POLICY_REQUIREMENT_CODES };

// ---------------------------------------------------------------------------
// Declarations
// ---------------------------------------------------------------------------

export const REQUIREMENT_OPERATIONS = ['REQUIRE', 'HIDE'] as const;

export type RequirementOperation = (typeof REQUIREMENT_OPERATIONS)[number];

/**
 * A requirement as declared by a configuration layer, before any occurrence is
 * evaluated against it. This is profile content; `ResolvedRequirement` is the
 * per-occurrence outcome.
 *
 * Discriminated on `operation` because a hide does not assert an authority or a
 * stage — it removes an advisory requirement an earlier layer declared. Giving
 * it those fields would invite them to be read as an assertion and checked
 * against the layer ceiling, which would reject legitimate hides.
 */
export type RequirementSpec =
  | {
      operation: 'REQUIRE';
      code: string;
      authority: RequirementAuthority;
      stage: GateStage;
      layer: ConfigurationLayer;
    }
  | {
      operation: 'HIDE';
      code: string;
      layer: ConfigurationLayer;
    };

/** The asserting variant. Only these end up in the effective set. */
export type RequiredSpec = Extract<RequirementSpec, { operation: 'REQUIRE' }>;

export interface CompositionConflict {
  code: string;
  layer: ConfigurationLayer;
  reason: string;
  detail?: string;
}

export interface CompositionResult {
  /** Publishable only when `conflicts` is empty. */
  requirements: readonly RequiredSpec[];
  conflicts: readonly CompositionConflict[];
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * Merges layer declarations into the effective requirement set.
 *
 * Specs are processed in configuration-layer order regardless of the order they
 * are supplied in, so a caller cannot change the outcome by reordering the
 * input array.
 */
export function composeRequirements(specs: readonly RequirementSpec[]): CompositionResult {
  const conflicts: CompositionConflict[] = [];
  const effective = new Map<string, RequiredSpec>();

  // Layer order first, then every REQUIRE within a layer before any HIDE in
  // that same layer. Sorting by layer alone is not enough: Array.sort is
  // stable, so two specs on the same layer keep their input order, and a HIDE
  // that arrived before its own layer's REQUIRE would report the requirement as
  // undeclared and then let the REQUIRE reinstate it. The same two declarations
  // would then publish or fail depending purely on array position.
  //
  // A HIDE still cannot reach a requirement added by a *later* layer, which is
  // intended: an earlier layer does not get to pre-empt a later one.
  const operationRank = (spec: RequirementSpec): number => (spec.operation === 'REQUIRE' ? 0 : 1);
  const ordered = [...specs].sort(
    (a, b) =>
      configurationLayerRank(a.layer) - configurationLayerRank(b.layer) ||
      operationRank(a) - operationRank(b),
  );

  for (const spec of ordered) {
    const existing = effective.get(spec.code);

    // A hide asserts nothing, so the authority ceiling does not apply to it.
    // Its only guard is that what it removes must be purely advisory, which
    // also stops it being used against a system-owned code (those are all
    // STATUTORY or PROGRAM, never OPTIONAL_CAPTURE).
    if (spec.operation === 'HIDE') {
      if (!existing) {
        // Hiding something nothing declared is a profile authoring mistake, not
        // a silent no-op.
        conflicts.push({
          code: spec.code,
          layer: spec.layer,
          reason: 'hides_undeclared_requirement',
        });
        continue;
      }
      if (existing.authority !== 'OPTIONAL_CAPTURE') {
        conflicts.push({
          code: spec.code,
          layer: spec.layer,
          reason: 'hides_blocking_requirement',
          detail: `${spec.code} is ${existing.authority}, only OPTIONAL_CAPTURE may be hidden`,
        });
        continue;
      }
      effective.delete(spec.code);
      continue;
    }

    // A layer may never assert an authority above its own ceiling.
    if (!canLayerAssertAuthority(spec.layer, spec.authority)) {
      conflicts.push({
        code: spec.code,
        layer: spec.layer,
        reason: 'layer_exceeds_authority_ceiling',
        detail: `${spec.layer} cannot assert ${spec.authority}`,
      });
      continue;
    }

    // A system-owned code may be tightened but never weakened.
    const owner = ownerAuthorityForRequirementCode(spec.code);
    if (owner && isStrongerAuthority(owner, spec.authority)) {
      conflicts.push({
        code: spec.code,
        layer: spec.layer,
        reason: 'weakens_system_owned_requirement',
        detail: `${spec.code} is owned by ${owner}, declared as ${spec.authority}`,
      });
      continue;
    }

    if (!existing) {
      effective.set(spec.code, spec);
      continue;
    }

    // Restrictive merge: keep the strongest authority and the earliest stage.
    // Neither can move in the permissive direction, so declaration order within
    // a layer cannot weaken an established requirement.
    const authority =
      requirementAuthorityRank(spec.authority) < requirementAuthorityRank(existing.authority)
        ? spec.authority
        : existing.authority;
    const stage =
      gateStageRank(spec.stage) < gateStageRank(existing.stage) ? spec.stage : existing.stage;

    effective.set(spec.code, {
      ...existing,
      authority,
      stage,
      // Attribute the merged requirement to whichever layer set the binding
      // constraint, so an explanation can name it.
      layer: authority === existing.authority ? existing.layer : spec.layer,
    });
  }

  return { requirements: [...effective.values()], conflicts };
}

/**
 * Profile publication gate. A profile with any conflict never reaches a worker.
 */
export function isPublishable(result: CompositionResult): boolean {
  return result.conflicts.length === 0;
}
